import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, getToken, setToken, authHeaders } from "@/lib/api";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminContacts from "@/pages/admin/AdminContacts";
import { LayoutDashboard, Inbox, Package, LogOut, ExternalLink, BarChart3, Mail } from "lucide-react";
import { BRAND } from "@/lib/brand";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "enquiries", label: "Buyer Enquiries", icon: Inbox },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "contacts", label: "Contact Leads", icon: Mail },
  { id: "products", label: "Products", icon: Package },
];

const StatCard = ({ label, value, testid }) => (
  <div className="bg-white border border-[#004225]/10 rounded-sm p-5" data-testid={testid}>
    <div className="text-[10px] font-eyebrow text-[#B5942D]">{label}</div>
    <div className="mt-1 font-display text-3xl text-[#004225]">{value}</div>
  </div>
);

const Overview = ({ stats }) => {
  if (!stats) return <div className="text-[#171717]/60 text-sm">Loading…</div>;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
        <StatCard label="Total Enquiries" value={stats.total} testid="stat-total" />
        <StatCard label="New" value={stats.new} testid="stat-new" />
        <StatCard label="This Month" value={stats.this_month} testid="stat-this-month" />
        <StatCard label="Negotiation" value={stats.negotiation} testid="stat-negotiation" />
        <StatCard label="Quotations Sent" value={stats.quotation_sent} testid="stat-quotations" />
        <StatCard label="Sample Requests" value={stats.sample_requested} testid="stat-samples" />
        <StatCard label="Orders Confirmed" value={stats.order_confirmed} testid="stat-orders" />
        <StatCard label="Products Live" value={stats.products_total} testid="stat-products" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
          <div className="font-display text-lg text-[#004225] mb-3">Enquiries by Country</div>
          {stats.by_country.length === 0 && <div className="text-sm text-[#171717]/50">No data yet</div>}
          {stats.by_country.map((c) => (
            <div key={c.country} className="flex justify-between py-2 border-b border-[#004225]/10 text-sm">
              <span>{c.country}</span><span className="font-semibold text-[#004225]">{c.count}</span>
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
          <div className="font-display text-lg text-[#004225] mb-3">Most Requested Products</div>
          {stats.top_products.length === 0 && <div className="text-sm text-[#171717]/50">No data yet</div>}
          {stats.top_products.map((p) => (
            <div key={p.name} className="flex justify-between py-2 border-b border-[#004225]/10 text-sm">
              <span className="truncate pr-4">{p.name}</span><span className="font-semibold text-[#004225]">{p.count}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
        <div className="font-display text-lg text-[#004225] mb-3">Recent Enquiries</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] font-eyebrow text-[#B5942D] border-b border-[#004225]/10">
              <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Buyer</th><th className="py-2 pr-4">Company</th><th className="py-2 pr-4">Country</th><th className="py-2">Status</th>
            </tr></thead>
            <tbody>
              {stats.recent.map((e) => (
                <tr key={e.id} className="border-b border-[#004225]/5">
                  <td className="py-2 pr-4 whitespace-nowrap">{(e.created_at || "").slice(0, 10)}</td>
                  <td className="py-2 pr-4">{e.name}</td>
                  <td className="py-2 pr-4">{e.company}</td>
                  <td className="py-2 pr-4">{e.country}</td>
                  <td className="py-2"><span className="text-[10px] font-eyebrow bg-[#F7F2E2] text-[#004225] px-2 py-1 rounded-full">{(e.status || "NEW").toUpperCase()}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    axios.get(`${API}/auth/me`, authHeaders())
      .then((r) => setMe(r.data))
      .catch(() => { setToken(null); nav("/admin/login"); });
  }, [nav]);

  useEffect(() => {
    if (tab === "overview") {
      axios.get(`${API}/admin/stats`, authHeaders()).then((r) => setStats(r.data)).catch(() => {});
    }
  }, [tab]);

  if (!me) return <div className="min-h-[60vh] flex items-center justify-center text-[#171717]/60">Checking access…</div>;

  return (
    <div className="min-h-screen bg-[#F4F4ED]" data-testid="admin-dashboard">
      <div className="bg-[#002B18] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="GN" className="h-10 w-10 rounded-full bg-white/10 p-0.5" />
            <div>
              <div className="font-display text-lg">GN Agro Traders — Admin</div>
              <div className="text-[10px] font-eyebrow text-[#D4AF37]">{me.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-white/70 hover:text-white"><ExternalLink className="h-3.5 w-3.5" />View Site</Link>
            <button onClick={() => { setToken(null); nav("/admin/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest hover:bg-white/10">
              <LogOut className="h-3.5 w-3.5" />Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 pb-0">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest rounded-t-sm transition-colors ${tab === t.id ? "bg-[#F4F4ED] text-[#004225]" : "text-white/70 hover:text-white"}`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === "overview" && <Overview stats={stats} />}
        {tab === "enquiries" && <AdminEnquiries />}
        {tab === "analytics" && <AdminAnalytics />}
        {tab === "contacts" && <AdminContacts />}
        {tab === "products" && <AdminProducts />}
      </div>
    </div>
  );
};

export default AdminDashboard;
